import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { EditorAudioPreview, LoopRegionBeats } from './useEditorAudioPreview';

/** Loop length (beats) used when looping without an explicit drag selection. */
export const DEFAULT_LOOP_BEATS = 4;
/** Shortest loop a drag can commit (beats); shorter drags snap up to default. */
export const MIN_LOOP_BEATS = 1;

/**
 * Whole-beat loop window between a drag anchor and the cursor, clamped into
 * `[0, totalBeats]`. A near-zero drag (a click) falls back to a default-length
 * loop so a single tap still loops something musical. Pure so both editors and
 * their default-region closures can share one implementation.
 */
export function buildLoopRegion(
  anchorBeat: number,
  cursorBeat: number,
  totalBeats: number,
): LoopRegionBeats {
  const clamp = (b: number) => Math.max(0, Math.min(b, totalBeats));
  let start = clamp(Math.round(Math.min(anchorBeat, cursorBeat)));
  let end = clamp(Math.round(Math.max(anchorBeat, cursorBeat)));
  if (end - start < MIN_LOOP_BEATS) {
    start = Math.min(start, Math.max(0, totalBeats - MIN_LOOP_BEATS));
    end = Math.min(totalBeats, start + DEFAULT_LOOP_BEATS);
  }
  return { startBeat: start, endBeat: end };
}

export interface UseEditorLoopUiArgs {
  preview: EditorAudioPreview;
  /** Beat forward playback starts from (Space / Play button). */
  playFromBeat: () => number;
  /** Loop window used when toggling the loop without a drag selection (L). */
  defaultLoopRegion: () => LoopRegionBeats;
  /** Surfaces preview decode / autoplay errors into the editor UI. */
  onError: (message: string) => void;
}

export interface EditorLoopUi {
  /** Toggle forward playback (Space / Play button). Stable identity. */
  togglePlay: () => void;
  /** Toggle the loop (L / Loop button). Stable identity. */
  toggleLoop: () => void;
  /** Live selection while dragging the loop strip (pre-commit); else null. */
  loopDraft: LoopRegionBeats | null;
  setLoopDraft: (region: LoopRegionBeats | null) => void;
  /** Draft if mid-drag, otherwise the committed loop region. */
  shownLoopRegion: LoopRegionBeats | null;
  /** Latest preview for once-attached native listeners (wheel / keydown). */
  previewRef: MutableRefObject<EditorAudioPreview>;
  /** Latest togglePlay / toggleLoop for the once-attached keydown listener. */
  togglePlayRef: MutableRefObject<() => void>;
  toggleLoopRef: MutableRefObject<() => void>;
}

/**
 * Editor-side glue shared by the Guitar + Pulse map creators on top of
 * {@link useEditorAudioPreview}: the play / loop toggles, the in-drag loop
 * draft, error surfacing, and the refs the editors' once-attached native
 * listeners read so they never go stale. Each editor supplies how it maps its
 * own view onto beats via {@link UseEditorLoopUiArgs.playFromBeat} /
 * {@link UseEditorLoopUiArgs.defaultLoopRegion}; everything else is identical.
 */
export function useEditorLoopUi({
  preview,
  playFromBeat,
  defaultLoopRegion,
  onError,
}: UseEditorLoopUiArgs): EditorLoopUi {
  const [loopDraft, setLoopDraft] = useState<LoopRegionBeats | null>(null);
  const shownLoopRegion = loopDraft ?? preview.loopRegion;

  // Page-supplied closures + the live preview kept in refs so the returned
  // toggles stay stable and native listeners read the latest state.
  const playFromBeatRef = useRef(playFromBeat);
  const defaultLoopRegionRef = useRef(defaultLoopRegion);
  const onErrorRef = useRef(onError);
  const previewRef = useRef(preview);
  useEffect(() => {
    playFromBeatRef.current = playFromBeat;
    defaultLoopRegionRef.current = defaultLoopRegion;
    onErrorRef.current = onError;
    previewRef.current = preview;
  });

  const togglePlay = useCallback(() => {
    previewRef.current.toggleForward(playFromBeatRef.current());
  }, []);
  const toggleLoop = useCallback(() => {
    previewRef.current.toggleLoop(defaultLoopRegionRef.current());
  }, []);

  const togglePlayRef = useRef(togglePlay);
  const toggleLoopRef = useRef(toggleLoop);
  useEffect(() => {
    togglePlayRef.current = togglePlay;
    toggleLoopRef.current = toggleLoop;
  });

  // Surface preview decode / autoplay failures in the editor's error box.
  useEffect(() => {
    if (preview.error) {
      onErrorRef.current(preview.error);
    }
  }, [preview.error]);

  return {
    togglePlay,
    toggleLoop,
    loopDraft,
    setLoopDraft,
    shownLoopRegion,
    previewRef,
    togglePlayRef,
    toggleLoopRef,
  };
}
